<template>
	<div>
		<template v-if="can_manage_education_levels">
			<div class="k-case-ie-line">
				<div class="k-case-ie-line-label">Education Level Range:</div>
				<div class="k-case-ie-line-body">
					<div class="k-ed-level-ranges-select">
						<k-autocomplete
							background-color="#fff"
							label=""
							outlined
							dense
							hide-details
							aria-label="Education Level Range"
							v-model="ss_framework_data.education_level_categories"
							:items="education_level_categories"
						/>
					</div>
					<k-btn v-if="ss_framework_data.education_level_categories && ss_framework_data.education_level_categories !== '__new_range__' && !is_official_education_level" x-small color="secondary" class="ml-2" @click="open_grades_dialog">
						Manage Ed Levels
					</k-btn>
				</div>
			</div>
		</template>

		<k-dialog v-model="import_grades_dialog" max-width="600">
			<k-card>
				<k-card-title class="d-flex align-center">
					<div>Import Education Levels from Another Framework</div>
					<k-spacer />
					<k-btn icon @click="import_grades_dialog = false">
						<k-icon>fas fa-times</k-icon>
					</k-btn>
				</k-card-title>

				<k-card-text>
					<div class="mb-3 k-ed-level-ranges-hint">
						Select a framework, then choose which education level ranges to import. Each imported range is an editable copy you can customize.
					</div>

					<k-autocomplete
						background-color="#fff"
						label="Source Framework"
						outlined
						dense
						hide-details
						v-model="import_grades_source_framework"
						:items="importable_frameworks"
						item-text="title"
						item-value="lsdoc_identifier"
						return-object
					/>

					<div v-if="import_grades_source_framework" class="mt-4">
						<div class="mb-2 font-weight-medium">Select Ranges to Import:</div>
						<div v-for="key in import_grades_available_keys" :key="key">
							<k-checkbox
								v-if="!has_education_level(key)"
								:input-value="import_grades_selected_keys.includes(key)"
								@change="toggle_import_grade_key(key)"
								:label="education_level_label(key)"
								hide-details
								dense
								class="mt-1"
							/>
							<div v-else class="d-flex align-center py-1 ml-1">
								<k-icon small class="mr-2" color="grey">fas fa-check</k-icon>
								<span class="k-ed-level-ranges-already-imported">{{ education_level_label(key) }} (already imported)</span>
							</div>
						</div>
					</div>
				</k-card-text>

				<k-card-actions>
					<k-spacer />
					<k-btn text @click="import_grades_dialog = false">Cancel</k-btn>
					<k-btn
						color="primary"
						:disabled="import_grades_selected_keys.length === 0"
						@click="do_import_grades"
					>
						<k-icon small class="mr-2">fas fa-file-import</k-icon>
						Import {{ import_grades_selected_keys.length }} Range(s)
					</k-btn>
				</k-card-actions>
			</k-card>
		</k-dialog>

		<k-dialog v-model="grades_dialog" max-width="900">
			<k-card>
				<k-card-title class="d-flex align-center">
					<div>
						Manage Ed Levels: <b>{{ form.values.name }}</b>
					</div>
					<k-spacer />
					<k-btn icon @click="grades_dialog = false">
						<k-icon>fas fa-times</k-icon>
					</k-btn>
				</k-card-title>

				<k-card-text>
					<k-text-field
						class="k-ed-level-ranges-name-field"
						background-color="#fff"
						outlined
						dense
						label="Range Name"
						v-bind="formFields.name"
						@input="formFields.name.input"
						@blur="formFields.name.blur"
					/>

					<div class="mt-4 k-ed-level-ranges-hint k-ed-level-ranges-hint-small">
						Define the grade levels for this range, in order from lowest to highest.
					</div>

					<div
						v-if="form.values.items.length > 0"
						class="k-ed-level-ranges-row mt-3"
					>
						<k-text-field
							class="k-ed-level-ranges-col-value"
							background-color="#f5f5f5"
							outlined
							dense
							hide-details
							label="Value"
							value="''"
							disabled
						/>
						<k-text-field
							class="k-ed-level-ranges-col-text"
							background-color="#f5f5f5"
							outlined
							dense
							hide-details
							label="Text"
							value="—"
							disabled
						/>
						<div class="k-ed-level-ranges-col-action-spacer"></div>
					</div>

					<div
						v-for="(item, i) in form.values.items.slice(1)"
						:key="i + 1"
						class="k-ed-level-ranges-row mt-3"
					>
						<k-text-field
							class="k-ed-level-ranges-col-value"
							background-color="#fff"
							outlined
							dense
							label="Value"
							v-bind="formField(`items.${i + 1}.value`)"
							@input="formHandleInput(`items.${i + 1}.value`, $event)"
							@blur="formHandleBlur(`items.${i + 1}.value`)"
						/>
						<k-text-field
							class="k-ed-level-ranges-col-text"
							background-color="#fff"
							outlined
							dense
							label="Text"
							v-bind="formField(`items.${i + 1}.text`)"
							@input="formHandleInput(`items.${i + 1}.text`, $event)"
							@blur="formHandleBlur(`items.${i + 1}.text`)"
						/>

						<k-btn icon color="red darken-2" @click="remove_grade_row(i + 1)">
							<k-icon small>fas fa-trash-alt</k-icon>
						</k-btn>
					</div>

					<div class="mt-4">
						<k-btn small color="primary" @click="add_grade_row">
							<k-icon small class="mr-2">fas fa-plus</k-icon>
							Add Row
						</k-btn>
					</div>
				</k-card-text>

				<k-card-actions>
					<k-btn v-if="!grades_form_is_new" color="red darken-2" dark @click="delete_grades_set">
						<k-icon small class="mr-2">fas fa-trash-alt</k-icon>Delete
					</k-btn>
					<k-spacer />
					<k-btn text @click="grades_dialog = false">Cancel</k-btn>
					<k-btn v-if="grades_form_is_new" color="secondary" @click="open_import_grades_dialog">
						<k-icon small class="mr-1">fas fa-file-import</k-icon>
						Import
					</k-btn>
					<k-btn color="primary" :loading="form.isSubmitting" :disabled="form.isSubmitting" @click="formHandleSubmit">
						<k-icon small class="mr-2">fas fa-save</k-icon>Save
					</k-btn>
				</k-card-actions>
			</k-card>
		</k-dialog>
	</div>
</template>

<script>
import { z } from 'zod'
import { useFormMixin } from '@cglt/ui-legacy'

const grades_form_schema = z.object({
	name: z.string().trim().min(2, 'Range name must be 2–50 characters.').max(50, 'Range name must be 2–50 characters.'),
	items: z.array(z.object({
		value: z.string().trim().max(250, 'Max 250 characters'),
		text: z.string().trim().max(250, 'Max 250 characters'),
	})),
}).superRefine((data, ctx) => {
	const editable = (data.items || []).slice(1)
	editable.forEach((item, i) => {
		if (!item.value) ctx.addIssue({ code: 'custom', path: ['items', i + 1, 'value'], message: 'Required' })
		else if (item.value === "''") ctx.addIssue({ code: 'custom', path: ['items', i + 1, 'value'], message: 'Duplicate value' })
		if (!item.text) ctx.addIssue({ code: 'custom', path: ['items', i + 1, 'text'], message: 'Required' })
	})

	const all_items = data.items || []
	for (const field of ['value', 'text']) {
		const counts = new Map()
		all_items.forEach(item => {
			const v = item[field]
			if (!v) return
			counts.set(v, (counts.get(v) || 0) + 1)
		})
		editable.forEach((item, i) => {
			const v = item[field]
			if (v && counts.get(v) > 1) {
				ctx.addIssue({ code: 'custom', path: ['items', i + 1, field], message: `Duplicate ${field}` })
			}
		})
	}
})

async function grades_form_on_submit(values, add_error) {
	const official_grades = this.$store.state.official_grades || {}
	const key = (values.name || '').trim()

	const matches_official = Object.keys(official_grades).find(k => k.toLowerCase() === key.toLowerCase())
	if (matches_official) {
		add_error('name', `Cannot use "${key}" as it conflicts with the official "${matches_official}" education level range. Please choose a different name.`)
		return
	}

	const original_key = this.ss_framework_data.education_level_categories
	const is_rename = !this.grades_form_is_new && original_key?.toLowerCase() === key.toLowerCase()
	const custom_collision = Object.keys(this.ss_framework_data.education_levels || {}).find(k => k.toLowerCase() === key.toLowerCase())
	if (!is_rename && custom_collision) {
		add_error('name', `A range named "${custom_collision}" already exists.`)
		return
	}

	const cleaned = values.items
		.map(x => {
			const value = $.trim(x.value || '')
			const text = $.trim(x.text || '')
			const value_unchanged = x._original_value !== undefined && x.value === x._original_value
			const abbreviation = (value_unchanged && x.abbreviation) ? $.trim(x.abbreviation) : value
			return { value, text, abbreviation }
		})
		.filter((x, i) => x.value || x.text || x.abbreviation || i === 0)
		.map((x, i) => ({ index: i, ...x }))

	const original_values = (this.ss_framework_data.education_levels?.[original_key] || [])
		.map(x => x.value)
		.filter(v => v)
	const current_values_set = new Set(cleaned.map(x => x.value).filter(v => v))

	const renames_by_old = new Map()
	values.items.forEach(x => {
		const new_value = $.trim(x.value || '')
		if (x._original_value && x._original_value !== new_value && new_value) {
			renames_by_old.set(x._original_value, new_value)
		}
	})

	const renames = original_values
		.filter(v => !current_values_set.has(v) && !this.approved_deletions.has(v) && renames_by_old.has(v))
		.map(old_value => ({ old_value, new_value: renames_by_old.get(old_value) }))

	if (renames.length > 0) {
		const cfitems = this.framework_record.json?.CFItems || []
		const orphaning = renames
			.map(c => ({
				...c,
				count: cfitems.filter(ci => Array.isArray(ci.educationLevel) && ci.educationLevel.includes(c.old_value)).length,
			}))
			.filter(c => c.count > 0)

		if (orphaning.length > 0) {
			const total = orphaning.reduce((sum, c) => sum + c.count, 0)
			const summary = orphaning.map(c => `"${c.old_value}" → "${c.new_value}" (${c.count})`).join(', ')
			const proceed = await this.$confirm({
				title: 'Rename education level values?',
				text: `${total} item(s) in this framework are tagged with values you're renaming: ${summary}. Those items will keep the old values and may not display correctly or appear in filters and crosswalks. Continue?`,
				acceptText: 'Save anyway',
				acceptColor: 'red darken-2',
			}).catch(() => false)
			if (!proceed) {
				orphaning.forEach(c => {
					const row_index = values.items.findIndex(x => x._original_value === c.old_value)
					if (row_index === -1) return
					const path = `items.${row_index}.value`
					this.formHandleInput(path, c.old_value)
					this.$set(this.form.fieldStates, path, { touched: false, dirty: false })
					this.$set(this.form.errors, path, undefined)
				})
				return
			}
		}
	}

	if (!this.grades_form_is_new && original_key && original_key !== key && this.ss_framework_data.education_levels?.[original_key]) {
		this.$delete(this.ss_framework_data.education_levels, original_key)
	}

	if (!this.ss_framework_data.education_levels) {
		this.$set(this.ss_framework_data, 'education_levels', {})
	}
	this.$set(this.ss_framework_data.education_levels, key, cleaned)

	this.ss_framework_data.education_level_categories = key
	this.grades_dialog = false
}

export default {
	name: 'EducationLevelRanges',
	mixins: [useFormMixin({ schema: grades_form_schema, onSubmit: grades_form_on_submit })],
	props: {
		ss_framework_data: { type: Object, required: true },
		framework_record: { type: Object, required: true },
	},
	data() {
		return {
			grades_dialog: false,
			grades_form_is_new: false,
			import_grades_dialog: false,
			import_grades_source_framework: null,
			import_grades_selected_keys: [],
			approved_deletions: new Set(),
		}
	},
	computed: {
		education_level_categories() {
			const official = Object.keys(this.$store.state.official_grades || {})
			const custom = Object.keys(this.ss_framework_data.education_levels || {})
			const all = [...new Set([...official, ...custom])].sort()
			const items = all.map(system => ({
				value: system,
				text: this.education_level_label(system),
			}))
			items.push({ value: '__new_range__', text: '-- NEW RANGE --' })
			return items
		},
		is_official_education_level() {
			const key = this.ss_framework_data.education_level_categories
			if (empty(key)) return false
			const official_grades = this.$store.state.official_grades || {}
			const custom_grades = this.ss_framework_data.education_levels || {}
			return !!official_grades[key] && !custom_grades[key]
		},
		can_manage_education_levels() {
			return vapp.is_granted('admin_framework', this.framework_record.lsdoc_identifier)
		},
		importable_frameworks() {
			return this.$store.state.framework_records
				.filter(fr => fr.lsdoc_identifier !== this.framework_record.lsdoc_identifier && fr.json.CFDocument.frameworkType !== 'crosswalk' && fr.ss_framework_data?.is_mirror !== 'yes')
				.map(fr => ({
					lsdoc_identifier: fr.lsdoc_identifier,
					title: fr.json.CFDocument.title,
					education_levels: fr.ss_framework_data?.education_levels || {}
				}))
		},
		import_grades_available_keys() {
			if (!this.import_grades_source_framework) return []
			const custom = Object.keys(this.import_grades_source_framework.education_levels || {})
			const official = Object.keys(this.$store.state.official_grades || {})
			return [...new Set([...custom, ...official])].sort()
		},
	},
	watch: {
		'ss_framework_data.education_level_categories'(val, old_val) {
			if (val === '__new_range__') {
				const prev = old_val || ''
				this.$nextTick(() => {
					this.ss_framework_data.education_level_categories = prev
				})
				this.reset_grades_form('', [{ value: '', text: '—', abbreviation: '' }])
				this.grades_form_is_new = true
				this.grades_dialog = true
			}
		},
		import_grades_source_framework() {
			this.import_grades_selected_keys = []
		},
	},
	methods: {
		reset_grades_form(name, items) {
			this.$set(this.form.values, 'name', name)
			this.$set(this.form.values, 'items', items)
			Object.keys(this.form.errors).forEach(k => this.$set(this.form.errors, k, undefined))
			Object.keys(this.form.fieldStates).forEach(k => this.$set(this.form.fieldStates, k, { touched: false, dirty: false }))
			this.approved_deletions = new Set()
		},

		open_grades_dialog() {
			const key = this.ss_framework_data.education_level_categories
			if (empty(key)) return

			if (this.is_official_education_level) {
				this.$alert('Official education level systems cannot be edited. Create a custom category to define your own education levels.')
				return
			}

			let existing = JSON.parse(JSON.stringify(this.ss_framework_data.education_levels?.[key] || []))
			if (existing.length === 0) {
				existing.push({ value: '', text: '—', abbreviation: '' })
			}
			existing = existing.map(x => ({ ...x, _original_value: x.value }))

			this.reset_grades_form(key, existing)
			this.grades_form_is_new = false
			this.grades_dialog = true
		},

		add_grade_row() {
			this.form.values.items.push({
				value: '',
				text: '',
				abbreviation: ''
			})
		},

		remove_grade_row(i) {
			const item = this.form.values.items[i]
			const old_value = item?._original_value

			if (old_value) {
				const cfitems = this.framework_record.json?.CFItems || []
				const count = cfitems
					.filter(ci => Array.isArray(ci.educationLevel) && ci.educationLevel.includes(old_value))
					.length

				if (count > 0) {
					this.$confirm({
						title: 'Remove education level?',
						text: `${count} item(s) in this framework are tagged with "${old_value}". Removing this row will leave them tagged with a value that no longer exists — they may not display correctly or appear in filters and crosswalks. Continue?`,
						acceptText: 'Remove anyway',
						acceptColor: 'red darken-2',
					}).then(y => {
						if (!y) return
						this.approved_deletions.add(old_value)
						this.form.values.items.splice(i, 1)
						if (this.formHasErrors) this.formValidateAll()
					})
					return
				}
			}

			this.form.values.items.splice(i, 1)
			if (this.formHasErrors) this.formValidateAll()
		},

		delete_grades_set() {
			const key = this.ss_framework_data.education_level_categories

			const range_values = new Set(
				(this.ss_framework_data.education_levels?.[key] || [])
					.map(x => x.value)
					.filter(v => v)
			)
			const cfitems = this.framework_record.json?.CFItems || []
			const orphan_count = cfitems
				.filter(ci => Array.isArray(ci.educationLevel) && ci.educationLevel.some(v => range_values.has(v)))
				.length
			const orphan_warning = orphan_count > 0
				? ` ${orphan_count} item(s) in this framework are tagged with values from this range and will be left referencing values that no longer exist — they may not display correctly or appear in filters and crosswalks.`
				: ''

			this.$confirm({
				title: 'Delete Education Level Range',
				text: `Are you sure you want to delete the "${key}" education level range?${orphan_warning} This action cannot be undone.`,
				acceptText: 'Delete',
				acceptColor: 'red darken-2',
			}).then(y => {
				if (!y) return

				if (this.ss_framework_data.education_levels && this.ss_framework_data.education_levels[key]) {
					this.$delete(this.ss_framework_data.education_levels, key)
					if (Object.keys(this.ss_framework_data.education_levels).length === 0) {
						this.ss_framework_data.education_levels = null
					}
				}

				const options = this.education_level_categories.filter(o => o.value !== '__new_range__')
				const us_option = options.find(o => o.value === 'US')
				this.ss_framework_data.education_level_categories = us_option ? 'US' : (options[0]?.value || '')

				this.grades_dialog = false
			})
		},

		education_level_label(key) {
			const official_grades = this.$store.state.official_grades || {}
			return official_grades[key] ? U.official_education_level_range_label(key, official_grades[key]) : key
		},

		has_education_level(key) {
			const levels = this.ss_framework_data.education_levels
			if (!levels) return false
			return !!(levels[key] || levels[`${key} (Custom)`])
		},

		toggle_import_grade_key(key) {
			const idx = this.import_grades_selected_keys.indexOf(key)
			if (idx === -1) {
				this.import_grades_selected_keys.push(key)
			} else {
				this.import_grades_selected_keys.splice(idx, 1)
			}
		},

		open_import_grades_dialog() {
			this.grades_dialog = false
			this.import_grades_source_framework = null
			this.import_grades_selected_keys = []
			this.import_grades_dialog = true
		},

		do_import_grades() {
			if (!this.import_grades_source_framework) return

			const custom_levels = this.import_grades_source_framework.education_levels || {}
			const official_levels = this.$store.state.official_grades || {}

			if (!this.ss_framework_data.education_levels) {
				this.$set(this.ss_framework_data, 'education_levels', {})
			}

			let imported_count = 0
			let first_imported_key = null
			for (const key of this.import_grades_selected_keys) {
				const source = custom_levels[key] || official_levels[key]
				if (!source) continue
				const level_data = JSON.parse(JSON.stringify(source))
				const save_key = official_levels[key] ? `${key} (Custom)` : key
				this.$set(this.ss_framework_data.education_levels, save_key, level_data)
				if (!first_imported_key) first_imported_key = save_key
				imported_count++
			}

			this.import_grades_dialog = false

			if (imported_count > 0) {
				this.ss_framework_data.education_level_categories = first_imported_key
				this.$inform(`Imported ${imported_count} range(s).`)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.k-ed-level-ranges-select {
	width: 310px;
}

.k-ed-level-ranges-name-field {
	max-width: 220px;
}

.k-ed-level-ranges-hint {
	font-size: 14px;
	color: #666;
}

.k-ed-level-ranges-hint-small {
	font-size: 12px;
}

.k-ed-level-ranges-already-imported {
	color: #999;
}

.k-ed-level-ranges-row {
	display: flex;
	align-items: center;
	gap: 8px;
}

.k-ed-level-ranges-col-value {
	max-width: 120px;
}

.k-ed-level-ranges-col-text {
	flex: 1 1 auto;
}

.k-ed-level-ranges-col-action-spacer {
	width: 36px;
}
</style>
